<template>
    <v-btn prepend-icon="mdi-chart-bar" color="primary" variant="text" @click="openDialog">
        {{ t('Gráficos') }}
    </v-btn>

    <v-dialog v-model="dialog" max-width="600px">
        <v-card>
            <v-toolbar flat>
                <v-toolbar-title>{{ t('Gráficos') }}</v-toolbar-title>
                <v-spacer />
                <v-btn icon @click="dialog = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>
            <div class="p-4 bg-white rounded-2xl shadow h-72 w-full">
                <h2 class="text-xl font-semibold ma-2">
                    Distribución de {{ variable }}
                </h2>

                <!-- only render the canvas once the dialog is open -->
                <div v-if="dialog" class="h-full">
                    <canvas ref="canvas" class="w-full h-full"></canvas>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref, watch, nextTick, onBeforeUnmount } from 'vue'
import { Chart, registerables } from 'chart.js'
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
Chart.register(...registerables)

const props = defineProps({
    data: { type: Array, required: true },
    variable: { type: String, required: true },
    bins: { type: Number, default: 10 }
})

const dialog = ref(false)
const canvas = ref(null)
let chart = null

const openDialog = () => {
    dialog.value = true
}

// helper to compute histogram
function computeHistogram() {
    const values = props.data
        .map(item => item[props.variable])
        .filter(v => v != null)
        .map(v => Number(v))
        .filter(v => !isNaN(v));

    if (!values.length) {
        return { labels: [], counts: [] };
    }

    const min = Math.min(...values);
    const max = Math.max(...values);

    // CASO ESPECIAL: todos los valores iguales
    if (min === max) {
        return {
            labels: [`${min.toFixed(1)}`],
            counts: [values.length]
        };
    }

    const binWidth = (max - min) / props.bins;
    const counts = Array(props.bins).fill(0);

    values.forEach(v => {
        let i = Math.floor((v - min) / binWidth);
        if (i >= props.bins) i = props.bins - 1;
        counts[i]++;
    });

    const labels = counts.map((_, i) => {
        const lo = min + i * binWidth;
        const hi = lo + binWidth;
        return `${lo.toFixed(1)} – ${hi.toFixed(1)}`;
    });

    return { labels, counts };
}



// watch dialog open/close
watch(dialog, async (isOpen) => {
    if (isOpen) {
        // wait for DOM
        await nextTick()

        const { labels, counts } = computeHistogram()
        const ctx = canvas.value.getContext('2d')

        chart = new Chart(ctx, {
            type: 'bar',
            data: {
                labels,
                datasets: [{
                    label: `Distribución de ${props.variable}`,
                    data: counts,
                    backgroundColor: 'rgba(54, 162, 235, 0.5)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1
                }]
            },
            options: {
                scales: {
                    x: { title: { display: true, text: props.variable } },
                    y: { title: { display: true, text: 'Count' }, beginAtZero: true }
                },
                responsive: true,
                maintainAspectRatio: false
            }
        })
    } else {
        // destroy chart when dialog closes
        if (chart) {
            chart.destroy()
            chart = null
        }
    }
})

onBeforeUnmount(() => {
    if (chart) {
        chart.destroy()
        chart = null
    }
})
</script>

<style scoped>
canvas {
    width: 100%;
    height: 100%;
}
</style>
